/**
 * Find the first *visible* element matching `selector`. A match is considered
 * visible when:
 *   - `offsetParent !== null` (i.e. not `display: none` and attached to layout),
 *     OR the element is `position: fixed` (in which case `offsetParent` is
 *     null even when fully visible — a quirk of the spec),
 *   - the bounding rect has non-zero width and height.
 *
 * This intentionally avoids `Element.checkVisibility()` because the project's
 * frontend has no `browserslist` declaration, so we keep to features that work
 * uniformly across the Chrome/Firefox/Safari versions the team currently runs.
 *
 * The filter exists to prevent the spotlight from latching onto a hidden
 * duplicate of a `data-tour` selector — `ReportActionBar` mounts three copies
 * of `[data-tour="cpty-report-actionbar"]` (edit / draft / sent) and during
 * mode transitions both the old and new copies are momentarily in the DOM.
 */
const findVisible = (selector: string): HTMLElement | null => {
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(selector),
  );
  for (const el of candidates) {
    if (el.offsetParent === null) {
      // position: fixed elements legitimately have null offsetParent. Fall
      // back to a computed-style check so fixed sidebars / overlays aren't
      // incorrectly skipped (e.g. `FilterSidebar` is `fixed inset-y-0 right-0`).
      if (getComputedStyle(el).position !== 'fixed') continue;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;
    return el;
  }
  return null;
};

export const waitForElement = (
  selector: string,
  timeoutMs = 2000,
): Promise<HTMLElement | null> => {
  const found = findVisible(selector);
  if (found) return Promise.resolve(found);

  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      const el = findVisible(selector);
      if (el) {
        observer.disconnect();
        clearTimeout(timer);
        resolve(el);
      }
    });
    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeoutMs);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      // Visibility-toggling refactors typically flip `style`/`class` rather
      // than add nodes; observe attribute changes too so we react to a
      // hidden element becoming visible without mounting a new one.
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  });
};

export const tourSelector = (dataTourValue: string): string =>
  `[data-tour="${dataTourValue}"]`;
